import { useState, useCallback } from 'react';
import {
  userPreferencesApi,
  factoriesApi,
  suppliersApi,
} from '../configs/api_endpoints.config';
import { apiService } from '../services/api.service';

export function useUserPreferences() {
  const [preferenceId, setPreferenceId] = useState(null);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedFactories, setSelectedFactories] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [savedSupplier, setSavedSupplier] = useState(null);
  const [savedFactories, setSavedFactories] = useState([]);
  const [savedDepartments, setSavedDepartments] = useState([]);
  const [savedCountries, setSavedCountries] = useState([]);

  const fetchPreferences = async () => {
    if (isLoadingPreferences) return;

    setIsLoadingPreferences(true);
    try {
      const response = await apiService.get(userPreferencesApi.global);

      const preferencesArray = response.data?.data || response.data;

      if (
        preferencesArray &&
        Array.isArray(preferencesArray) &&
        preferencesArray.length > 0
      ) {
        const preferenceData = preferencesArray[0];
        setPreferenceId(preferenceData.id);

        if (
          preferenceData.suppliers &&
          Array.isArray(preferenceData.suppliers) &&
          preferenceData.suppliers.length > 0
        ) {
          const supplierId = preferenceData.suppliers[0].id;
          const vendorId = preferenceData.suppliers[0].vendor_id;

          const supplierResponse = await apiService.get(suppliersApi.search, {
            offset: 0,
            limit: 10,
            search: vendorId,
            includes: 'factories',
          });

          const suppliersArray =
            supplierResponse.data?.data || supplierResponse.data;

          if (
            suppliersArray &&
            Array.isArray(suppliersArray) &&
            suppliersArray.length > 0
          ) {
            const supplier = suppliersArray.find((s) => s.id === supplierId);

            if (supplier) {
              setSelectedSupplier(supplier);
              setSavedSupplier(supplier);

              const preferenceDepartments =
                preferenceData.departments &&
                Array.isArray(preferenceData.departments)
                  ? preferenceData.departments.map((d) =>
                      typeof d === 'string'
                        ? { id: d, name: d }
                        : { id: d.id, name: d.name || d.id },
                    )
                  : [];

              if (preferenceDepartments.length > 0) {
                setSelectedDepartments(preferenceDepartments);
                setSavedDepartments(preferenceDepartments);
              }

              if (
                preferenceData.factories &&
                Array.isArray(preferenceData.factories) &&
                supplier.factories &&
                Array.isArray(supplier.factories)
              ) {
                const factoryIds = preferenceData.factories.map((f) => f.id);

                const matchedFactories = supplier.factories.filter((f) =>
                  factoryIds.includes(f.id),
                );

                setSelectedFactories(matchedFactories);
                setSavedFactories(matchedFactories);

                if (
                  preferenceData.countries &&
                  Array.isArray(preferenceData.countries)
                ) {
                  const savedCountryData = preferenceData.countries || [];

                  const factoryDataPromises = matchedFactories.map(
                    async (factory) => {
                      const searchId =
                        factory.dss_id || factory.factory_id || factory.id;
                      if (!searchId) return { departments: [], countries: [] };

                      try {
                        const response = await apiService.get(
                          factoriesApi.search,
                          {
                            offset: 0,
                            limit: 10,
                            search: searchId,
                          },
                        );

                        const factoriesArray =
                          response.data?.data || response.data;

                        if (
                          factoriesArray &&
                          Array.isArray(factoriesArray) &&
                          factoriesArray.length > 0
                        ) {
                          const factoryData = factoriesArray[0];
                          const departments = factoryData.departments || [];

                          const countries = [];
                          if (factoryData.port_of_loading) {
                            countries.push(factoryData.port_of_loading);
                          }

                          return { departments, countries };
                        }
                        return { departments: [], countries: [] };
                      } catch (error) {
                        console.error(
                          `Error fetching data for factory ${factory.dss_id}:`,
                          error,
                        );
                        return { departments: [], countries: [] };
                      }
                    },
                  );

                  const allFactoryData = await Promise.all(factoryDataPromises);

                  const allCountries = allFactoryData.flatMap(
                    (data) => data.countries,
                  );

                  const uniqueCountries = allCountries.reduce(
                    (acc, country) => {
                      if (!acc.find((c) => c.id === country.id)) {
                        acc.push(country);
                      }
                      return acc;
                    },
                    [],
                  );

                  const matchedCountries = uniqueCountries.filter((c) =>
                    savedCountryData.some(
                      (saved) =>
                        saved.name === c.name ||
                        saved.port_code === c.id ||
                        saved.portCode === c.id,
                    ),
                  );

                  setSelectedCountries(matchedCountries);
                  setSavedCountries(matchedCountries);
                }
              }
            } else {
              console.warn('Supplier not found in search results');
            }
          } else {
            console.warn('No supplier data returned from search API');
          }
        } else {
          console.log('No suppliers found in preference data');
        }
      } else {
        console.log(
          'No existing preferences found (this is normal for first time users)',
        );
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const savePreferences = async () => {
    try {
      const supplierId = selectedSupplier?.id || '';
      const factoryIds = selectedFactories.map((f) => f.id).join(',');
      const departmentIds = selectedDepartments.map((d) => d.id).join(',');

      const countries = selectedCountries.map((c) => ({
        name: c.name,
        portCode: c.id,
      }));

      const payload = {};

      if (supplierId) {
        payload.suppliers = supplierId;
      }

      if (factoryIds) {
        payload.factories = factoryIds;
      }

      if (departmentIds) {
        payload.departments = departmentIds;
      }

      if (countries.length > 0) {
        payload.countries = countries;
      }

      let response;
      if (preferenceId) {
        response = await apiService.put(
          `${userPreferencesApi.global}/${preferenceId}`,
          payload,
        );
      } else {
        response = await apiService.post(userPreferencesApi.global, payload);
        if (response.data && response.data.id) {
          setPreferenceId(response.data.id);
        }
      }

      setSavedSupplier(selectedSupplier);
      setSavedFactories(selectedFactories);
      setSavedDepartments(selectedDepartments);
      setSavedCountries(selectedCountries);

      return { success: true };
    } catch (error) {
      console.error('Error saving preferences:', error);
      return { success: false, error };
    }
  };

  const resetToSaved = useCallback(() => {
    setSelectedSupplier(savedSupplier);
    setSelectedFactories(savedFactories);
    setSelectedDepartments(savedDepartments);
    setSelectedCountries(savedCountries);
  }, [savedSupplier, savedFactories, savedDepartments, savedCountries]);

  return {
    preferenceId,
    isLoadingPreferences,
    selectedSupplier,
    setSelectedSupplier,
    selectedFactories,
    setSelectedFactories,
    selectedDepartments,
    setSelectedDepartments,
    selectedCountries,
    setSelectedCountries,
    savedSupplier,
    savedFactories,
    savedDepartments,
    savedCountries,
    fetchPreferences,
    savePreferences,
    resetToSaved,
  };
}
